import {Fragment, useEffect, useState} from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Hidden,
  Link as MuiLink,
} from '@mui/material';

import axios from 'axios';
import useSWR from 'swr';

import Skeleton from '@mui/material/Skeleton';

import Error from './../../../_error';

import BottomNav from '../../../../components/BottomNav';
import CompactRequestCard from '../../../../components/CompactRequestCard';
import HeroUnder from '../../../../components/HeroUnder';
import PersonalisedEstimate from '../../../../components/PersonalisedEstimate';
import RequestCard from '../../../../components/RequestCard';

import SearchEstimates from '../../../../components/SearchEstimates';
import TopNav from '../../../../components/TopNav';
import WaivingBuddy from '../../../../components/WaivingBuddy';

import { NextLinkComposed } from '../../../../components/Link';

import bodyCG from '../../../../public/partners/v2/500x200-compare-guru-scratch-dent-cover.png';
import mobileCG from '../../../../public/partners/v2/325x80-compare-guru-insurance.png';
import tyresCG from '../../../../public/partners/v2/500x200-compare-guru-tyre-rim-cover.png';
import warrantyCG from '../../../../public/partners/v2/500x200-compare-guru-pre-owned-warranty.png';


const DynamicCostEstimateSummary = dynamic(() => import ('../../../../components/CostEstimateSummary'));


const ads = {
  warranty: {
    img: warrantyCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/pre-owned-motor-warranty/personal?channel=mechanic-buddy',
  },
  tyres: {
    img: tyresCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/tyre-and-rim-guard/personal?channel=mechanic-buddy',
  },
  body: {
    img: bodyCG,
    href: 'https://compareguru.co.za/insurance-quotes/motorvaps/scratch-and-dent/personal?channel=mechanic-buddy',
  },
}


function CostEstimate({ data, statusCode }) {
  
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [adType, setAdType] = useState(ads['warranty']);

  const { data: requestData } = useSWR(
    router.query && `/content/jobs/?make=${router.query.car}&jobcategory=${data?.job_type.category}`
  );

  useEffect(() => {
    if (data?.job_type.category == "Tyres, Wheels and Alignment") {
      setAdType(ads['tyres']);
    }

    if (data?.job_type.category == "Bodywork and Dent Repairs" || data?.job_type.category == "Windows, Windscreens and Mirrors") {
      setAdType(ads['body']);
    }
  }, [data])

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (statusCode) {
    return <Error statusCode={statusCode} />
  }

  const pageTitle = `${router.query.car} ${router.query.model} ${router.query.service} Cost Estimate | MechanicBuddy`;
  const pageDescription = `An estimated cost of R${data.min_estimate} to R${data.max_estimate}, based on ${data.model_service_count} past quotes. The middlemost quote was R${data.model_service_median}`;

  return <>
    <Head>
      <title>{pageTitle}</title>
      <meta
        name="description"
        content={pageDescription}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://www.mechanicbuddy.co.za${router.asPath}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {/* Add a dynamically generated cool image here */}
      <meta property="og:image" content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:url" content={`https://www.mechanicbuddy.co.za${router.asPath}`} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg" />

      <link rel="canonical" href={`https://www.mechanicbuddy.co.za${router.asPath}`} />
    </Head>

    <TopNav color="transparent" />

    <HeroUnder
      title={`${router.query.car} ${router.query.model} ${router.query.service}`}
      description="Generated cost estimate based on past quotes for the car and service you selected."
    />
    
    <Container
      maxWidth="lg"
      sx={{
        mt: '-120px',
        mb: 8
      }}
    >
      <Grid
        container
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={5}
        // Fix spacing issue on mobile
      >
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <DynamicCostEstimateSummary
                make={router.query.car}
                model={router.query.model}
                service={router.query.service}
                estimate={data}
              />
            </CardContent>

            <Box
              width="100%"
              display={{xs: "flex", md: "none"}}
              flexDirection="row"
              justifyContent="center"
            >
              <MuiLink
                href="https://compareguru.co.za/insurance-quotes/car-insurance/personal?channel=mechanic-buddy"
                target="_blank"
                rel="noopener noreferrer nofollow"
                sx={{ mb: -1 }}
              >
                <Image
                  src={mobileCG}
                  alt="CompareGuru insurance"
                  quality={100}
                />
              </MuiLink>
            </Box>

            <Box
              width="100%"
              bgcolor="primary.main"
              p={3}
              display={{xs: "none", md: "flex"}}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box color="white">Not exactly the type of estimate you are looking for?</Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleClickOpen()}
              >Search Estimates</Button>
            </Box>
          </Card>

          <Box
            sx={{
              bgcolor: {
                xs: "none",
                md: "white",
              }
            }}
          >
            <Box
              sx={{
                p: 2,
                pt: 4,
                fontSize: "h6.fontSize",
              }}>
              See what quotes other people requested
            </Box>
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block"
                }
              }}
            >
              {requestData && requestData.results.map((job, index, arr) => (
                <Fragment key={job.id}>
                  <Divider />
                  <RequestCard job={job} />
                </Fragment>
              ))}
            </Box>

            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none"
                }
              }}
            >
              {requestData && requestData.results.map((job, index, arr) => (
                <CompactRequestCard key={job.id} job={job} />
              ))}
            </Box>
            {requestData && requestData.results.length > 0 &&
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="secondary"
                  component={NextLinkComposed}
                  to={{
                    pathname: '/cost-estimates/',
                    query: {
                      make: router.query.car,
                      jobcategory: data.job_type.category,
                    },
                  }}
                  sx={{ my: 2 }}
                >View More</Button>
              </Box>
            }
            {!requestData &&
              <Box
                sx={{
                  '& .MuiSkeleton-root': {
                    mb: 2
                  },
                }}
              >
                <Skeleton variant="rectangular" width="100%" height={200} />
                <Skeleton variant="rectangular" width="100%" height={200} />
                <Skeleton variant="rectangular" width="100%" height={200} />
              </Box>
            }
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Hidden mdDown implementation="css">
            <a
              href={adType.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={adType.img}
                alt="CompareGuru"
                quality={100}
              />
            </a>

            <Box p={2} />

            {router.query.car &&
              <PersonalisedEstimate
                initialMake={router.query.car}
                initialModel={router.query.model}
                initialService={router.query.service}
              />
            }
          </Hidden>
        </Grid>
      </Grid>
    </Container>

    <WaivingBuddy />

    <BottomNav />

    <SearchEstimates data={{}} open={open} handleClose={handleClose} />
  </>;
}

// consider using getStaticProps
export async function getStaticProps({ params }) {
  const props = await axios(
    `${process.env.DOMAIN}/content/cost-estimate/`,
    { params }
  )
  .then(result => {
    return {
      data: result.data,
      statusCode: null
    }
  })
  .catch(error => {
    return {
      data: null,
      statusCode: error.response.status
    }
  });

  if (props.statusCode === 404) {
    return {
      notFound: true
    }
  }

  return {
    props,
    revalidate: 86400, // In seconds
  }
}

export async function getStaticPaths() {
  const { data } = await axios(`${process.env.DOMAIN}/api/v2/calculated-estimates/`);

  const paths = data.map(estimate => ({
    params: {
      car: estimate.make,
      model: estimate.model,
      service: estimate.job_type,
    }
  }))

  return {
    paths,
    fallback: 'blocking'
  };
}

export default CostEstimate;
